import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn } from '@angular/router';
import { Store } from '@ngxs/store';
import {
  AuthenticationSelectors,
  Plant,
  SetActivePlant,
  SetUserRightsByRole,
} from '@orion-platform/core';
import { filter, firstValueFrom, Observable, of, take, timeout } from 'rxjs';
import { ResetAllDefaultFields } from '../features/scm/material-management/v1/store/common/common.actions';
import { ResetPlantDependentCache } from '../features/scm/material-management/v1/store/supply-visibility/supply-visibility.actions';
import { PLANTS_WAIT_MS } from './plant-from-url.consts';

/**
 * Puts the reader in the plant the address names, before the page is built.
 *
 * A link out of an email carries its plant in the path — `/ls51/…` — and the
 * reader who opens it may be sitting in another one. Every request the page
 * then makes goes out under `Request-Tenants`, so a page built first and
 * switched afterwards asks the wrong back end and finds nothing: the record it
 * was sent for does not exist in the plant it asked.
 *
 * Hence a guard rather than something watching the router: `canActivate` runs
 * before the route's components exist, so the first request already carries the
 * right plant, and nothing has to be thrown away and fetched again.
 *
 * An address naming a plant the reader has no rights to is left alone — they
 * stay where they are rather than being moved somewhere they would be refused.
 * @param {ActivatedRouteSnapshot} route The route being entered.
 * @returns {Promise<boolean>} Always true: this decides which plant, never whether.
 */
export const plantFromUrlGuard: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
): Promise<boolean> => {
  const store = inject(Store);

  const requested = route.paramMap.get('plant');
  if (!requested) return true;

  const plants = await firstValueFrom(
    store.select(AuthenticationSelectors.plants).pipe(
      filter((list: Plant[]): boolean => !!list?.length),
      take(1),
      // On a browser that has never logged in the list arrives late; on one
      // where it never arrives, the page is better than a wait with no end.
      timeout({ first: PLANTS_WAIT_MS, with: (): Observable<Plant[]> => of([]) }),
    ),
  );

  const match = plants.find(
    (plant: Plant): boolean =>
      String(plant.Code).toLowerCase() === requested.toLowerCase() && !!plant.Show,
  );

  if (!match) return true;

  const active = store.selectSnapshot(AuthenticationSelectors.getActiveplant);
  if (String(active).toLowerCase() === String(match.Code).toLowerCase()) return true;

  // In this order: the plant first, then what the reader may do in it, then
  // everything that was read under the plant they came from.
  await firstValueFrom(store.dispatch(new SetActivePlant(match.Code)));
  await firstValueFrom(
    store.dispatch([
      new SetUserRightsByRole(),
      new ResetPlantDependentCache(),
      new ResetAllDefaultFields(),
    ]),
  );

  return true;
};
